import { Box, Link, Typography } from "@mui/material";
import { InfoGroup } from "Components/Contact/InfoGroup";
import { toTelHref } from "Infrastructure/Utils/SupportUtils";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import type { ReactNode } from "react";

type TextWithLinkProps = {
  text: string;
  linkText: string;
  href: string;
};

const getHunterContacts = (t: TFunction, phone: string) => ({
  email: t(Resources.Support.Contact.HunterEmail),
  phone,
  customInfo: (
    <Box display="flex" gap={1} flexWrap="wrap" rowGap={0} mb={1}>
      {t(Resources.Pages.Content.Contact.HunterDiscord.Description)}
      <Link
        href="https://discord.gg/TtDQRr7NvH"
        target="_blank"
        fontWeight={500}
        sx={{ textDecoration: "inherit" }}
      >
        {t(Resources.Pages.Content.Contact.HunterDiscord.Link)}
      </Link>
    </Box>
  ),
});

const TextWithLink = ({ text, linkText, href }: TextWithLinkProps) => (
  <Box display="flex" gap={1}>
    {text}
    <Link href={href} fontWeight={500} sx={{ textDecoration: "inherit" }}>
      {linkText}
    </Link>
  </Box>
);

const getContacts = (userRole: AccessRoleCode, t: TFunction) => {
  // The app's one support line and address — the Support dialog reads the same keys.
  const phone = t(Resources.Support.Contact.PhoneNumber);
  const email = t(Resources.Support.Contact.Email);
  const hunterContacts = getHunterContacts(t, phone);
  const userRoleToContactInfo: Record<
    AccessRoleCode,
    { email: string; customInfo?: ReactNode; phone?: string }
  > = {
    [AccessRoleCodes.Customer]: { email },
    [AccessRoleCodes.Merchant]: { email, phone },
    [AccessRoleCodes.Admin]: { email, phone },
    [AccessRoleCodes.AdminLite]: { email, phone },
    [AccessRoleCodes.Hunter]: hunterContacts,
    [AccessRoleCodes.HunterPlus]: hunterContacts,
    [AccessRoleCodes.Aml]: { email, phone },
    [AccessRoleCodes.Employee]: {
      email: "",
      customInfo: undefined,
      phone: undefined,
    },
    [AccessRoleCodes.CustomerOnboarding]: { email, phone },
    [AccessRoleCodes.Disponent]: { email },
    [AccessRoleCodes.Child]: { email },
  };

  return userRoleToContactInfo[userRole];
};

type Props = {
  userRole: AccessRoleCode;
};

export const ContactInfo = ({ userRole }: Props) => {
  const { t } = useResource();
  const { email, phone, customInfo } = getContacts(userRole, t);

  return (
    <InfoGroup>
      {customInfo}
      <TextWithLink
        text={t(Resources.Pages.Content.Contact.Email)}
        linkText={email}
        href={`mailto:${email}`}
      />
      {phone && (
        <>
          <TextWithLink
            text={t(Resources.Pages.Content.Contact.Phone)}
            linkText={phone}
            href={toTelHref(phone)}
          />
          <Typography
            sx={({ palette }) => ({
              fontSize: 13,
              mt: 1,
              color: palette.text.secondary,
            })}
          >
            {t(Resources.Pages.Content.Contact.PhoneDescription)}
          </Typography>
        </>
      )}
    </InfoGroup>
  );
};
